import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Menu, X, Phone, Mail, MapPin } from 'lucide-react'

const FALLBACK_MENU = [
  { label: 'Forside', link_slug: 'forside', order: 1 },
  { label: 'Mad ud af huset', link_slug: 'mad-ud-af-huset', order: 2 },
  { label: 'Catering', link_slug: 'catering', order: 3 },
  { label: 'Tapas', link_slug: 'tapas', order: 4 },
  { label: 'Klassisk dansk mad', link_slug: 'klassisk-dansk-mad', order: 5 },
  { label: 'Kager & Brød', link_slug: 'kager-og-brod', order: 6 },
  { label: 'Galleri', link_slug: 'galleri', order: 7 },
  { label: 'Om os', link_slug: 'om-os', order: 8 },
  { label: 'Kontakt', link_slug: 'kontakt', order: 9 }
]

export default function Header() {
  const [menuItems, setMenuItems] = useState([])
  const [isOpen, setIsOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const location = useLocation()

  useEffect(() => {
    // Fetch menu items from CMS
    fetch('https://api.dev.auxo.dk/public/content/menu_items')
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch menu')
        return res.json()
      })
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          const sorted = [...data].sort((a, b) => (a.order || 0) - (b.order || 0))
          setMenuItems(sorted)
        } else {
          setMenuItems(FALLBACK_MENU)
        }
      })
      .catch(() => {
        setMenuItems(FALLBACK_MENU)
      })

    // Handle scroll effect
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true)
      } else {
        setScrolled(false)
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Close mobile menu when route changes
  useEffect(() => {
    setIsOpen(false)
  }, [location])

  const isActive = (slug) => {
    if (slug === 'forside') {
      return location.pathname === '/'
    }
    return location.pathname === `/${slug}`
  }

  const getLinkPath = (slug) => {
    return slug === 'forside' ? '/' : `/${slug}`
  }

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 border-b ${
        isOpen
          ? 'bg-[#faf9f6] py-3 border-stone-200'
          : scrolled
          ? 'bg-[#faf9f6]/95 backdrop-blur-md py-3 border-stone-200/80 shadow-xs'
          : 'bg-[#faf9f6]/90 backdrop-blur-sm py-4 border-stone-200/40'
      }`}
    >
      {/* Utility Top Bar - Only visible on desktop (>= 1200px) */}
      <div className="mx-auto max-w-7xl px-6 pb-2 text-[10px] tracking-widest uppercase text-stone-600 flex justify-between border-b border-stone-200 max-[1200px]:hidden">
        <div className="flex items-center gap-6">
          <span className="flex items-center gap-1.5">
            <Phone size={10} className="text-[#b89a6c]" />
            Tlf: <a href="tel:+45489981" className="hover:text-[#b89a6c] transition-colors whitespace-nowrap">45 48 99 81</a>
          </span>
          <span className="flex items-center gap-1.5">
            <Mail size={10} className="text-[#b89a6c]" />
            Email: <a href="mailto:craft.line25@gmail.com" className="hover:text-[#b89a6c] transition-colors whitespace-nowrap">craft.line25@gmail.com</a>
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <MapPin size={10} className="text-[#b89a6c]" />
          <span>Vejlevej 10, 8700 Horsens</span>
        </div>
      </div>

      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 pt-2">
        {/* Logo Wordmark (Stacked Serif) */}
        <Link to="/" className="flex flex-col group">
          <span className="font-serif text-lg tracking-widest text-[#1c2b21] sm:text-xl uppercase font-light leading-none">
            CRAFT CAFÉ
          </span>
          <span className="text-[9px] font-sans font-light uppercase tracking-[0.3em] text-[#b89a6c] mt-1 transition-colors group-hover:text-[#1c2b21]">
            café & catering
          </span>
        </Link>

        {/* Desktop Navigation (Visible at >= 1200px) */}
        <nav className="hidden min-[1200px]:flex items-center gap-1">
          {menuItems.map((item) => (
            <Link
              key={item.id || item.link_slug}
              to={getLinkPath(item.link_slug)}
              className={`px-3.5 py-2 text-[11px] font-sans font-medium uppercase tracking-widest transition-colors cursor-pointer ${
                isActive(item.link_slug)
                  ? 'text-[#b89a6c] border-b border-[#b89a6c]/60'
                  : 'text-stone-600 hover:text-[#b89a6c]'
              }`}
            >
              {item.label}
            </Link>
          ))}
          
          {/* CTA Button - Solid Champagne, Sharp Corners */}
          <Link
            to="/kontakt"
            className="ml-6 bg-[#b89a6c] text-white px-5 py-2.5 text-[10px] font-sans font-semibold uppercase tracking-widest transition-all hover:bg-[#1c2b21] active:scale-95 cursor-pointer rounded-none shadow-none"
          >
            Kontakt
          </Link>
        </nav>

        {/* Mobile Menu Button (Visible at < 1200px) */}
        <div className="flex items-center gap-3 min-[1200px]:hidden">
          {/* Quick Call CTA */}
          <a
            href="tel:+45489981"
            className="flex items-center gap-1.5 bg-[#b89a6c]/10 px-3.5 py-1.5 text-[10px] font-sans font-semibold uppercase tracking-widest text-[#b89a6c] border border-[#b89a6c]/20 whitespace-nowrap rounded-none"
          >
            <Phone size={10} />
            Ring op
          </a>

          <button
            onClick={() => setIsOpen(!isOpen)}
            className="p-2 text-stone-600 hover:text-[#b89a6c] focus:outline-none cursor-pointer"
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation Drawer (Visible at < 1200px) */}
      {isOpen && (
        <div className="fixed inset-x-0 top-[65px] bottom-0 z-30 bg-[#faf9f6] bg-opacity-100 animate-fade-in min-[1200px]:hidden flex flex-col justify-between p-6 border-t border-stone-200">
          <nav className="flex flex-col gap-1">
            {menuItems.map((item) => (
              <Link
                key={item.id || item.link_slug}
                to={getLinkPath(item.link_slug)}
                className={`px-4 py-3 text-xs font-sans font-light uppercase tracking-widest transition-colors ${
                  isActive(item.link_slug)
                    ? 'bg-[#b89a6c]/10 text-[#b89a6c] border-l-2 border-[#b89a6c]'
                    : 'text-stone-600 hover:bg-stone-100 hover:text-[#b89a6c]'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Mobile Footer Info */}
          <div className="border-t border-stone-200 pt-6 space-y-5">
            <Link
              to="/kontakt"
              className="flex w-full items-center justify-center bg-[#b89a6c] py-3.5 text-center text-[10px] font-sans font-semibold uppercase tracking-widest text-white transition-all hover:bg-[#1c2b21] cursor-pointer rounded-none"
            >
              Bestil / Kontakt os
            </Link>
            
            <div className="grid grid-cols-2 gap-4 text-[10px] uppercase tracking-wider text-stone-600">
              <div className="space-y-1">
                <p className="font-light text-stone-500">Ring direkte</p>
                <a href="tel:+45489981" className="text-[#b89a6c] font-semibold whitespace-nowrap">45 48 99 81</a>
              </div>
              <div className="space-y-1">
                <p className="font-light text-stone-500">Send e-mail</p>
                <a href="mailto:craft.line25@gmail.com" className="text-[#b89a6c] font-semibold whitespace-nowrap">craft.line25@gmail.com</a>
              </div>
            </div>
            <p className="text-[9px] uppercase tracking-widest text-center text-stone-500">
              Vejlevej 10, 8700 Horsens
            </p>
          </div>
        </div>
      )}
    </header>
  )
}
